// OrgGraphTab — data-loading host for the org chart inside `/admin/people`.
//
// Owns nothing beyond fetch/loading/error/empty state — the rendering,
// filtering, and interaction live in `<OrgGraph>` (the composition root).

import { useCallback, useEffect, useState } from 'react';
import type { IOrgGraphResponse } from '@skystone/shared';
import { orgGraphApi } from '@/services/org-graph-api';
import { getErrorMessage } from '@/lib/utils';
import { ErrorBanner, PageLoading } from '@/components/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { OrgGraph } from '@/components/org-graph/OrgGraph';

export function OrgGraphTab() {
  const [data, setData] = useState<IOrgGraphResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await orgGraphApi.get();
      setData(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load org graph'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && data === null) {
    return <PageLoading />;
  }

  if (error !== null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Org graph</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ErrorBanner message={error} />
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-border px-3 py-1 text-sm hover:bg-muted"
          >
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  if (data === null || data.nodes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Org graph</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="py-8 text-center text-sm text-muted-foreground">
            No directory data available. The Entra sync may not have run yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <OrgGraph data={data} />;
}
